import classNames from 'classnames';
import localeData from 'dayjs/plugin/localeData';
import Icon from '@/components/atoms/icon/icon';
import Logo from '@/components/atoms/logo/logo';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import HubspotFormItem from '@/components/modules/hubspot-form/hubspot-form-item/hubspot-form-item';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import { getLogoName } from '@/libraries/getters/get-logo-name';
import FooterBackground from './footer-background/footer-background';
import styles from './footer.module.scss';

export type FooterProps = Content.Components['FooterDto'] & ModuleWrapperProps;

const socialMediaIconsMap: Record<
  Content.Components['SocialMediaLinkType'],
  IconName
> = {
  facebook: 'SocialFacebook',
  instagram: 'SocialInstagram',
  youtube: 'SocialYoutube',
};

const paymentMethodMap: Record<
  Content.Components['PaymentMethodType'],
  LogoName
> = {
  dankort: 'Dankort',
  mastercard: 'Mastercard',
  visa: 'Visa',
};

const socialMediaLabelMap: Record<
  Content.Components['SocialMediaLinkType'],
  string
> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
};

const getAccessibleLabel = (
  label: string | null | undefined,
  fallback: string,
) => {
  const trimmedLabel = label?.trim();

  return trimmedLabel || fallback;
};

const Footer = ({
  contactColumn,
  copyrightText,
  footerLogos,
  footerNavigation,
  homePageLink,
  hubSpotForm,
  paymentMethods,
  sloganText,
  socialMediaLinks,
  ...rest
}: FooterProps) => {
  const { t } = useI18n();
  const { brand } = usePageDetailsContext();
  const { dayjs } = useDateFormatter([localeData]);

  const renderLinkColumn = (
    columns: Content.Components['FooterNavigationSectionDto'][],
  ) => (
    <div className={styles.column}>
      {columns.map((item, itemIndex) => {
        return (
          <div key={itemIndex} className={styles.group}>
            <Typography className={styles.headline} tagStyle="bodyLargeBold">
              {item.headline}
            </Typography>

            <ul className={styles.list}>
              {item.links.map((link, linkIndex) => (
                <li key={linkIndex}>
                  <AnchorLink
                    className={styles.anchorLink}
                    icon="ArrowRight"
                    {...link}
                  />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );

  const hasFormOrSlogan = hubSpotForm || sloganText;

  return (
    <footer className={styles.component}>
      <FooterBackground />

      <ModuleWrapper {...rest} className={styles.wrapper} padding="none">
        <Container className={styles.container}>
          <div className={classNames(styles.row, styles.isGrid)}>
            <div className={styles.column}>
              <Link
                aria-label={
                  homePageLink
                    ? getAccessibleLabel(
                        homePageLink.text,
                        t('items.common.words.homepage'),
                      )
                    : t('items.common.words.homepage')
                }
                className={classNames(styles.logo, styles.isBrand)}
                {...homePageLink}
              >
                <Logo
                  name={getLogoName({
                    brand,
                    type: 'Footer',
                  })}
                />
              </Link>

              <div className={styles.links}>
                <Typography
                  className={styles.headline}
                  tagStyle="bodyLargeBold"
                >
                  {contactColumn.headline}
                </Typography>

                <Link
                  className={classNames(styles.link, styles.isUnderlined)}
                  url={`mailto:${contactColumn.email}`}
                >
                  <Icon className={styles.icon} name="Email" size={16} />

                  <Typography tagStyle="bodyMedium">
                    {contactColumn.email}
                  </Typography>
                </Link>

                <Link
                  className={styles.link}
                  url={`tel:${contactColumn.phoneNumber}`}
                >
                  <Icon className={styles.icon} name="Phone" size={16} />

                  <Typography tagStyle="bodyMedium">
                    {contactColumn.phoneNumber}
                  </Typography>
                </Link>

                {contactColumn.openingHoursContent && (
                  <div className={styles.workingHours}>
                    <Typography
                      className={styles.headline}
                      tagStyle="bodyMediumBold"
                    >
                      {contactColumn.openingHoursContent.title}
                    </Typography>

                    <div className={styles.days}>
                      {contactColumn.openingHoursContent.openingHours.map(
                        (day, dayIndex) => (
                          <div key={dayIndex}>
                            <Typography
                              className={styles.weekday}
                              tagStyle="bodyMedium"
                            >
                              {`${dayjs.weekdays(false)[day.dayOfWeek]}: `}
                            </Typography>

                            <Typography
                              className={styles.hours}
                              tagStyle="bodyMedium"
                            >
                              {day.value}
                            </Typography>
                          </div>
                        ),
                      )}
                    </div>

                    <RichText
                      text={contactColumn.openingHoursContent.noteText}
                    />
                  </div>
                )}
              </div>

              <div className={styles.address}>
                <Typography
                  className={styles.headline}
                  tagStyle="bodyLargeBold"
                >
                  {contactColumn.addressContent.title}
                </Typography>

                <RichText text={contactColumn.addressContent.address} />
              </div>
            </div>

            {renderLinkColumn(footerNavigation.firstColumn)}

            {renderLinkColumn(footerNavigation.secondColumn)}

            {renderLinkColumn(footerNavigation.thirdColumn)}
          </div>

          <div
            className={classNames(styles.row, {
              [styles.isEmpty]: !hasFormOrSlogan,
            })}
          >
            {hasFormOrSlogan && (
              <Row className={styles.middleRow}>
                {hubSpotForm && (
                  <Column width={{ laptop: 8 }}>
                    <HubspotFormItem form={hubSpotForm} noPadding />
                  </Column>
                )}

                {sloganText && (
                  <Column width={{ laptop: hubSpotForm ? 4 : 12 }}>
                    <Typography
                      className={styles.headline}
                      tagStyle="displayLarge"
                    >
                      {sloganText}
                    </Typography>
                  </Column>
                )}
              </Row>
            )}
          </div>

          <div className={classNames(styles.row, styles.isSpacedBetween)}>
            <div className={styles.left}>
              {footerLogos.rejsegarantifonden.link && (
                <div className={styles.brandGroup}>
                  <a
                    aria-label={getAccessibleLabel(
                      footerLogos.rejsegarantifonden.text,
                      'Rejsegarantifonden',
                    )}
                    href={footerLogos.rejsegarantifonden.link.url}
                    target={footerLogos.rejsegarantifonden.link.target}
                  >
                    <div
                      className={classNames(
                        styles.logo,
                        styles.isRajsengarantifonden,
                      )}
                    >
                      <Logo name="RejsegarantiFonden" />
                    </div>

                    <Typography
                      className={styles.headline}
                      tagStyle="bodySmallBold"
                    >
                      {footerLogos.rejsegarantifonden.text}
                    </Typography>
                  </a>
                </div>
              )}

              {footerLogos.trustPilot.link && (
                <div className={styles.brandGroup}>
                  <a
                    aria-label={getAccessibleLabel(
                      footerLogos.trustPilot.link.text,
                      'Trustpilot',
                    )}
                    href={footerLogos.trustPilot.link.url}
                    target={footerLogos.trustPilot.link.target}
                  >
                    <div
                      className={classNames(styles.logo, styles.isTrustpilot)}
                    >
                      <Logo name="Trustpilot" />
                    </div>
                  </a>
                </div>
              )}

              <div className={styles.socialMedia}>
                {socialMediaLinks.map(
                  ({ link, socialMediaType }, socialMediaLinkIndex) => (
                    <a
                      key={socialMediaLinkIndex}
                      aria-label={getAccessibleLabel(
                        link.text,
                        socialMediaLabelMap[socialMediaType],
                      )}
                      className={styles.socialMediaLink}
                      href={link.url}
                      target={link.target}
                    >
                      <Icon
                        className={classNames(
                          styles.icon,
                          styles.isSocialMedia,
                        )}
                        name={socialMediaIconsMap[socialMediaType]}
                        size={24}
                      />
                    </a>
                  ),
                )}
              </div>
            </div>

            <div className={styles.right}>
              <div className={styles.essentialLinks}>
                {footerNavigation.essentialLinks.map(
                  ({ text, ...link }, linkIndex) => (
                    <Link
                      key={linkIndex}
                      className={classNames(
                        styles.link,
                        styles.isUnderlined,
                        styles.isEssential,
                      )}
                      {...link}
                    >
                      <Typography tagStyle="bodySmall">{text}</Typography>
                    </Link>
                  ),
                )}

                {copyrightText && (
                  <Typography tagStyle="bodySmall">{copyrightText}</Typography>
                )}
              </div>

              <div className={styles.paymentMethods}>
                {paymentMethods.map((paymentMethod, paymentMethodIndex) => (
                  <div
                    key={paymentMethodIndex}
                    className={classNames(styles.logo, styles.isPaymentMethod)}
                  >
                    <Logo name={paymentMethodMap[paymentMethod.type]} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </ModuleWrapper>
    </footer>
  );
};

Footer.displayName = 'Footer';

export default Footer;
